import { useState } from "react";
import { Link } from "react-router-dom";
import { Sun, ArrowLeft, MailCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const IMG =
  "https://images.unsplash.com/photo-1516117172878-fd2c41f4a759?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxzbW9reSUyMG1vdW50YWlucyUyMHN1bnJpc2UlMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzgzOTA0NjQ2fDA&ixlib=rb-4.1.0&q=85";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If that email exists, a reset link is on its way.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFAFA] font-body">
      <div className="hidden lg:block relative grain">
        <img src={IMG} alt="Sunrise" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-slate-900/40" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <Sun className="h-10 w-10 text-amber-400" />
          <h2 className="mt-5 font-display text-4xl font-bold leading-tight">
            {'"He restoreth my soul."'}
          </h2>
          <p className="mt-2 text-slate-300">{"Psalm 23:3 — Let's get you back in."}</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/login" data-testid="back-login-link" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-amber-700 transition-colors duration-200">
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>
          <div className="mt-8">
            <img src="/logo.png" alt="Tennessee Web & Design" className="h-12 w-auto" />
          </div>

          {sent ? (
            <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="forgot-sent">
              <span className="grid place-items-center h-12 w-12 rounded-full bg-amber-50 text-amber-600 mx-auto"><MailCheck className="h-6 w-6" /></span>
              <h1 className="mt-5 font-display text-2xl font-bold text-slate-900">Check your inbox</h1>
              <p className="mt-2 text-slate-600">If an account exists for <span className="font-medium">{email}</span>, we've sent a link to reset your password. The link expires in 1 hour.</p>
              <Link to="/login" className="mt-6 inline-block rounded-full bg-amber-600 hover:bg-amber-700 text-white font-semibold px-6 py-3 transition-colors duration-200">Return to sign in</Link>
            </div>
          ) : (
            <>
              <h1 className="mt-8 font-display text-3xl font-bold text-slate-900 tracking-tight">Forgot password?</h1>
              <p className="mt-2 text-slate-600">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={submit} data-testid="forgot-form" className="mt-8 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                  <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" className="auth-input" placeholder="you@church.org" />
                </div>
                <button type="submit" disabled={loading} data-testid="forgot-submit-btn" className="w-full rounded-full bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-semibold py-3.5 transition-colors duration-200">
                  {loading ? "Sending..." : "Send Reset Link"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      <style>{`
        .auth-input { width:100%; border:1px solid hsl(214 20% 90%); border-radius:0.6rem; padding:0.75rem 0.9rem; font-size:0.9rem; background:#fff; box-shadow: inset 0 1px 2px rgba(0,0,0,0.03); transition:border-color .2s, box-shadow .2s; }
        .auth-input:focus { outline:none; border-color:hsl(32 95% 44%); box-shadow:0 0 0 3px hsl(32 95% 44% / 0.15); }
      `}</style>
    </div>
  );
}
